import { FC, useEffect, useState } from 'react'
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons'
import PrimaryButton from '@organisms/PrimaryButton'
import SecondaryButton from '@organisms/SecondaryButton'
import Select, { Option } from '@organisms/Select'
import { FinalTestQuestionInput } from '@models/courseTheme'
import {
  ModalWrapper,
  ModalTitle,
  FieldGroup,
  FieldLabel,
  StyledTextarea,
  TypeSelectWrapper,
  AnswersSectionTitle,
  AnswersColumnsHeader,
  CorrectColHeader,
  AnswerColHeader,
  AnswersList,
  AnswerRow,
  AnswerInput,
  CorrectRadioWrapper,
  RemoveAnswerBtn,
  AddAnswerBtn,
  ButtonsRow,
  ErrorText,
} from './styled'

type Props = {
  initialQuestion?: FinalTestQuestionInput
  isOpen: boolean
  onSave: (question: FinalTestQuestionInput) => void
  onSaveAndAddMore: (question: FinalTestQuestionInput) => void
}

const MIN_REQUIRED_ANSWERS = 2
const createEmptyAnswers = () => [
  { text: '', isCorrect: true },
  { text: '', isCorrect: false },
  { text: '', isCorrect: false },
]
type QuestionTypeValue = 'single' | 'multi'
type QuestionTypeOption = Option<QuestionTypeValue>

const QUESTION_TYPES: QuestionTypeOption[] = [
  { value: 'single', label: 'Один правильный' },
  { value: 'multi', label: 'Несколько правильных' },
]

const FinalTestQuestionFormModal: FC<Props> = ({
  initialQuestion,
  isOpen,
  onSave,
  onSaveAndAddMore,
}) => {
  const [questionText, setQuestionText] = useState(initialQuestion?.question ?? '')
  const [type, setType] = useState<QuestionTypeValue>(initialQuestion?.type ?? 'single')
  const [answers, setAnswers] = useState(
    initialQuestion?.answers?.length ? initialQuestion.answers : createEmptyAnswers()
  )
  const [errors, setErrors] = useState<{
    question?: string
    type?: string
    answers?: string
  }>({})
  const isEditing = !!initialQuestion

  useEffect(() => {
    if (!isOpen) {
      return
    }

    setQuestionText(initialQuestion?.question ?? '')
    setType(initialQuestion?.type ?? 'single')
    setAnswers(initialQuestion?.answers?.length ? initialQuestion.answers : createEmptyAnswers())
    setErrors({})
  }, [isOpen, initialQuestion])

  const handleAddAnswer = () => {
    setAnswers((prev) => [...prev, { text: '', isCorrect: false }])
  }

  const handleRemoveAnswer = (index: number) => {
    setAnswers((prev) => prev.filter((_, i) => i !== index))
  }

  const handleAnswerTextChange = (index: number, text: string) => {
    setAnswers((prev) => prev.map((a, i) => (i === index ? { ...a, text } : a)))
  }

  const handleTypeChange = (newType: QuestionTypeValue) => {
    setType(newType)

    if (newType === 'single') {
      setAnswers((prev) => {
        const firstCorrectIndex = prev.findIndex((answer) => answer.isCorrect)
        if (firstCorrectIndex < 0) {
          return prev
        }

        return prev.map((answer, index) => ({
          ...answer,
          isCorrect: index === firstCorrectIndex,
        }))
      })
    }
  }

  const handleTypeSelectChange = (option: QuestionTypeOption | null) => {
    if (!option) {
      return
    }

    handleTypeChange(option.value)
  }

  const handleCorrectChange = (index: number, checked: boolean) => {
    if (type === 'single') {
      setAnswers((prev) => prev.map((a, i) => ({ ...a, isCorrect: i === index })))
    } else {
      setAnswers((prev) => prev.map((a, i) => (i === index ? { ...a, isCorrect: checked } : a)))
    }
  }

  const validate = (): boolean => {
    const newErrors: { question?: string; type?: string; answers?: string } = {}
    const filledAnswers = answers.filter((answer) => answer.text.trim())

    if (!questionText.trim()) {
      newErrors.question = 'Введите текст вопроса'
    }

    if (filledAnswers.length < MIN_REQUIRED_ANSWERS) {
      newErrors.answers = 'Заполните минимум два варианта ответа'
    } else if (!filledAnswers.some((a) => a.isCorrect)) {
      newErrors.answers = 'Отметьте хотя бы один правильный ответ'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSave = (saveAndAddMore: boolean) => {
    if (!validate()) return

    const preparedAnswers = answers
      .filter((answer) => answer.text.trim())
      .map((answer) => ({ ...answer, text: answer.text.trim() }))

    const payload: FinalTestQuestionInput = {
      question: questionText.trim(),
      type,
      answers: preparedAnswers,
    }

    if (saveAndAddMore) {
      onSaveAndAddMore(payload)
      setQuestionText('')
      setType('single')
      setAnswers(createEmptyAnswers())
      setErrors({})
      return
    }

    onSave(payload)
  }

  return (
    <ModalWrapper>
      <ModalTitle>{initialQuestion ? 'Редактировать вопрос' : 'Добавить вопрос'}</ModalTitle>

      <FieldGroup>
        <FieldLabel>Вопрос*</FieldLabel>
        <StyledTextarea
          value={questionText}
          onChange={(e) => setQuestionText(e.target.value)}
          placeholder="Вопрос"
          rows={3}
          className={errors.question ? 'has-error' : ''}
        />
        {errors.question && <ErrorText>{errors.question}</ErrorText>}
      </FieldGroup>

      <FieldGroup>
        <TypeSelectWrapper>
          <Select
            options={QUESTION_TYPES}
            value={QUESTION_TYPES.find((questionType) => questionType.value === type)}
            placeholder="Тип вопроса"
            onChange={handleTypeSelectChange}
            isDisabled={isEditing}
            isSearchable={false}
            isClearable={false}
            menuPosition="fixed"
            withoutFetchOptions
            withoutFillSelected
          />
        </TypeSelectWrapper>
        {errors.type && <ErrorText>{errors.type}</ErrorText>}
      </FieldGroup>

      <div>
        <AnswersSectionTitle>Варианты ответов</AnswersSectionTitle>
        <AnswersColumnsHeader>
          <CorrectColHeader>Верный</CorrectColHeader>
          <AnswerColHeader>Вариант ответа</AnswerColHeader>
        </AnswersColumnsHeader>
        <AnswersList>
          {answers.map((answer, index) => (
            <AnswerRow key={index}>
              <CorrectRadioWrapper>
                <input
                  type={type === 'multi' ? 'checkbox' : 'radio'}
                  name="final-test-correct"
                  checked={answer.isCorrect}
                  onChange={(e) => handleCorrectChange(index, e.target.checked)}
                />
              </CorrectRadioWrapper>
              <AnswerInput
                value={answer.text}
                onChange={(e) => handleAnswerTextChange(index, e.target.value)}
                placeholder={`Вариант ${index + 1}`}
                className={errors.answers ? 'has-error' : ''}
              />
              {answers.length > 2 && (
                <RemoveAnswerBtn
                  type="button"
                  onClick={() => handleRemoveAnswer(index)}
                  title="Удалить вариант"
                >
                  <DeleteOutlined />
                  Удалить
                </RemoveAnswerBtn>
              )}
            </AnswerRow>
          ))}
        </AnswersList>
        <AddAnswerBtn type="button" onClick={handleAddAnswer}>
          <PlusOutlined /> Добавить
        </AddAnswerBtn>
        {errors.answers && <ErrorText>{errors.answers}</ErrorText>}
      </div>

      <ButtonsRow>
        <SecondaryButton type="button" onClick={() => handleSave(false)} autoWidth>
          Сохранить и выйти
        </SecondaryButton>
        <PrimaryButton type="button" onClick={() => handleSave(true)} autoWidth>
          Сохранить и добавить еще
        </PrimaryButton>
      </ButtonsRow>
    </ModalWrapper>
  )
}

export default FinalTestQuestionFormModal
